// Extracts plain text from an uploaded resume document.
// Supports PDF (.pdf), Word (.docx), and plain text (.txt / .md).

import mammoth from 'mammoth'
import * as pdfjsLib from 'pdfjs-dist'

// pdf.js needs a worker. Point it at a CDN build that matches the installed
// version so parsing works in the browser without bundler worker config.
pdfjsLib.GlobalWorkerOptions.workerSrc =
  'https://unpkg.com/pdfjs-dist@4.4.168/build/pdf.worker.min.mjs'

export const ACCEPTED_EXTENSIONS = ['.pdf', '.docx', '.txt', '.md']
export const ACCEPT_ATTR =
  '.pdf,.docx,.txt,.md,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain'

export const MAX_FILE_BYTES = 10 * 1024 * 1024 // 10 MB

export class UnsupportedFileError extends Error {
  constructor(name: string) {
    super(
      `"${name}" is not a supported format. Please upload a PDF, Word (.docx), or text file. For older .doc files, export to PDF or .docx first.`,
    )
    this.name = 'UnsupportedFileError'
  }
}

function getExtension(fileName: string): string {
  const idx = fileName.lastIndexOf('.')
  return idx === -1 ? '' : fileName.slice(idx).toLowerCase()
}

async function extractFromPdf(file: File): Promise<string> {
  const buffer = await file.arrayBuffer()
  const pdf = await pdfjsLib.getDocument({ data: buffer }).promise
  let text = ''
  for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
    const page = await pdf.getPage(pageNum)
    const content = await page.getTextContent()
    const pageText = content.items
      .map((item: any) => ('str' in item ? item.str : ''))
      .join(' ')
    text += pageText + '\n\n'
  }
  return text.trim()
}

async function extractFromDocx(file: File): Promise<string> {
  const buffer = await file.arrayBuffer()
  const result = await mammoth.extractRawText({ arrayBuffer: buffer })
  return (result.value || '').trim()
}

async function extractFromText(file: File): Promise<string> {
  return (await file.text()).trim()
}

export async function extractTextFromFile(file: File): Promise<string> {
  if (file.size > MAX_FILE_BYTES) {
    throw new Error(
      'That file is larger than 10 MB. Please upload a smaller document.',
    )
  }

  const ext = getExtension(file.name)

  if (ext === '.pdf' || file.type === 'application/pdf') {
    return extractFromPdf(file)
  }
  if (
    ext === '.docx' ||
    file.type ===
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ) {
    return extractFromDocx(file)
  }
  if (ext === '.txt' || ext === '.md' || file.type.startsWith('text/')) {
    return extractFromText(file)
  }

  throw new UnsupportedFileError(file.name)
}
